"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, Building2, Star, Link2, FileText, Code as Code2, ArrowRight, CircleCheck as CheckCircle2, Sparkles, UserCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
const localServices = [{
  id: "gbp-optimisation",
  title: "Google Business Profile Optimisation",
  description: "Claim, verify, and execute strategic Google Business Profile SEO to secure consistent visibility in Google's Local 3-Pack and Google Maps.",
  icon: MapPin,
  badge: "Local Maps",
  deliverables: ["Map Pack positioning strategy", "Category & attributes alignment", "Geotagged image & updates publishing"]
}, {
  id: "citations-directories",
  title: "Local Citations & Directories",
  description: "Build and clean NAP (Name, Address, Phone) citations across premier UK and industry-specific directory platforms.",
  icon: Building2,
  badge: "Authority",
  deliverables: ["Complete NAP consistency audit", "High-authority local directory submissions", "Duplicate & conflicting listing suppression"]
}, {
  id: "review-management",
  title: "Review Generation & Management",
  description: "Establish systematic strategies to earn authentic customer reviews that enhance map rankings and convert prospects.",
  icon: Star,
  badge: "Reputation",
  deliverables: ["Ethical review request workflows", "Brand response strategy & guidance", "Local trust signal enhancement"]
}, {
  id: "local-link-building",
  title: "Local Link Building",
  description: "Earn relevant, high-quality backlinks from regional businesses, local press, sponsorships, and community partners.",
  icon: Link2,
  badge: "Relevance",
  deliverables: ["Targeted regional outreach", "Community sponsorship leverage", "Hyper-local contextual link insertion"]
}, {
  id: "local-content-strategy",
  title: "Local Content Strategy",
  description: "Develop high-converting location pages and targeted local search optimisation resources that directly address nearby buyer intent.",
  icon: FileText,
  badge: "Content",
  deliverables: ["Location-specific service pages", "Hyper-local guides & FAQs", "Geographic search intent mapping"]
}, {
  id: "schema-markup",
  title: "Local Schema Markup",
  description: "Deploy robust LocalBusiness JSON-LD structured data so search engines accurately map your precise service areas.",
  icon: Code2,
  badge: "Technical",
  deliverables: ["LocalBusiness JSON-LD setup", "Geo-coordinate & areaServed markup", "Multi-location structured data"]
}];
export default function Localseo() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const cardVariants = {
    hidden: shouldReduceMotion ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="localseoservicesgrid" data-nav="light" className="relative bg-background text-foreground py-20 md:py-28 overflow-hidden isolate">
      <div className="absolute top-0 right-1/4 size-96 bg-accent/10 rounded-full blur-3xl pointer-events-none -z-10" />
      <div className="absolute bottom-10 left-10 size-80 bg-primary/5 rounded-full blur-2xl pointer-events-none -z-10" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-16 md:mb-20">
          <div className="inline-flex items-center gap-2 mb-4">
            <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider bg-secondary text-secondary-foreground border border-border">
              <Sparkles className="size-3.5 text-accent mr-1" />
              Targeted Local Search
            </Badge>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground font-serif mb-6">
            Local SEO Services Built for High-Intent Regional Growth
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed font-sans">
            Focused local search optimisation delivering Google Business Profile enhancement, clean directory citations, local schema structured data, and targeted geographic content to connect your business with high-intent nearby customers.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {localServices.map((service, i) => {
          const IconComponent = service.icon;
          return <motion.div key={service.id} data-index={i} variants={cardVariants} className="h-full flex">
                <Card className="flex flex-col justify-between w-full bg-card text-card-foreground border border-border rounded-xl shadow-xs hover:shadow-md hover:border-primary/40 transition-all duration-300 group">
                  <CardHeader className="p-6 pb-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 rounded-lg bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                        <IconComponent className="size-6" />
                      </div>
                      <Badge variant="outline" className="text-xs font-medium text-muted-foreground border-border">
                        {service.badge}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-semibold font-serif text-foreground group-hover:text-primary transition-colors">
                      {service.title}
                    </CardTitle>
                    <CardDescription className="text-sm text-muted-foreground leading-relaxed mt-2 font-sans">
                      {service.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="p-6 pt-0 flex-grow">
                    <div className="border-t border-border pt-4 mt-2">
                      <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-3">
                        What I deliver:
                      </p>
                      <ul className="space-y-2.5">
                        {service.deliverables.map((item, idx) => <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                            <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                            <span>{item}</span>
                          </li>)}
                      </ul>
                    </div>
                  </CardContent>

                  <div className="px-6 pb-6 pt-2">
                    <Button asChild variant="ghost" size="sm" className="w-full justify-between text-primary font-medium hover:bg-primary/5 hover:text-primary p-0 h-auto py-2">
                      <Link to="/ContactPage" className="inline-flex items-center justify-between w-full">
                        <span>Discuss this service</span>
                        <ArrowRight className="size-4 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </Button>
                  </div>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        <div className="mt-16 md:mt-20 p-8 md:p-10 rounded-2xl bg-muted/60 border border-border shadow-xs flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-full bg-accent/15 text-accent shrink-0 hidden sm:flex">
              <UserCheck className="size-6" />
            </div>
            <div>
              <h3 className="text-lg md:text-xl font-bold font-serif text-foreground mb-1">
                Ongoing Local Ranking & Competitor Tracking
              </h3>
              <p className="text-sm md:text-base text-muted-foreground max-w-2xl font-sans">
                Keep your Google Business Profile optimised, citations consistent across key directories, and map pack rankings protected against nearby competitors with structured local search maintenance.
              </p>
            </div>
          </div>
          <div className="shrink-0 w-full md:w-auto">
            <Button asChild size="lg" className="w-full md:w-auto bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm font-semibold">
              <Link to="/ContactPage">
                Book a Free SEO Consultation
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}